import { FC, ReactNode } from 'react';
import { cn } from '@/utils/classNames.ts';
import Timer from '@/components/ui/Timer/Timer';

type PageHeaderProps = {
  title: string;
  className?: string;
  backgroundClassName?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  disableBackButton?: boolean;
  titleAlign?: 'left' | 'center' | 'right';
  attemptStartedAt?: string;
  timeAllowedMinutes?: number | null;
  onTimeUp?: () => void;
  children?: ReactNode;
  rightContent?: ReactNode;
};

const PageHeader: FC<PageHeaderProps> = ({
  title,
  className,
  backgroundClassName,
  showBackButton = false,
  onBackClick,
  disableBackButton = false,
  titleAlign = 'left',
  attemptStartedAt,
  timeAllowedMinutes,
  onTimeUp,
  children,
  rightContent,
}) => {
  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          'w-full px-6 py-3 rounded-[20px] border-[0.72px] border-[#CCCCCC] border-opacity-50',
          backgroundClassName || 'bg-white',
          {
            'flex items-center gap-4': showBackButton,
            'pr-4': showBackButton && titleAlign === 'right',
          }
        )}
      >
        {showBackButton && onBackClick && (
          <button
            onClick={onBackClick}
            disabled={disableBackButton}
            className={cn(
              'w-12 h-12 flex items-center justify-center rounded-xl transition-colors shrink-0',
              disableBackButton
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#2B3440] text-white hover:bg-[#3d4559]'
            )}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
        )}

        <div className="flex-1 flex flex-col">
          <h1
            className={cn('text-[32px] leading-[63px] font-varela font-normal text-text-primary', className, {
              'text-left': titleAlign === 'left',
              'text-center': titleAlign === 'center',
              'text-right': titleAlign === 'right',
            })}
          >
            {title}
          </h1>
          {children}
        </div>

        {rightContent && <div className="flex items-center shrink-0">{rightContent}</div>}
      </div>

      {/* Timer component displayed separately below the header */}
      {attemptStartedAt && timeAllowedMinutes && (
        <Timer attemptStartedAt={attemptStartedAt} timeAllowedMinutes={timeAllowedMinutes} onTimeUp={onTimeUp} />
      )}
    </div>
  );
};

export default PageHeader;
